import React, { useMemo, useState } from 'react';
import { ActivityIcon, CircleDotIcon, DatabaseIcon, SatelliteIcon } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { AlertFeed } from '../components/AlertFeed';
import { CustomsHolds } from '../components/CustomsHolds';
import { KpiRail } from '../components/KpiRail';
import { OperationsHeader } from '../components/OperationsHeader';
import { PortCongestion } from '../components/PortCongestion';
import { ShipmentsTable } from '../components/ShipmentsTable';
import { WorldMap } from '../components/WorldMap';
import { initialAlerts, shipments, vessels } from '../data/operations';
import { useScreenInit } from '../useScreenInit.js';
type Filter = 'All' | 'Critical' | 'At risk' | 'Customs hold';
export function CommandCenterPage() {
  useScreenInit();
  const reduceMotion = useReducedMotion();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('All');
  const [alerts, setAlerts] = useState(initialAlerts);
  const [selectedVessel, setSelectedVessel] = useState<string | null>('v2');
  const [selectedShipment, setSelectedShipment] = useState<string | null>('GSX-847291');
  const visibleShipments = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return shipments.filter(shipment => {
      const matchesFilter = filter === 'All' || shipment.status === filter;
      const searchable = `${shipment.id} ${shipment.container} ${shipment.origin} ${shipment.destination} ${shipment.vessel} ${shipment.cargo}`.toLowerCase();
      return matchesFilter && (!normalized || searchable.includes(normalized));
    });
  }, [filter, query]);
  const visibleVessels = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return vessels;
    const matches = vessels.filter(vessel => `${vessel.name} ${vessel.route}`.toLowerCase().includes(normalized));
    return matches.length ? matches : vessels;
  }, [query]);
  return <div className="min-h-screen w-full bg-[#050b11] text-slate-300" data-id="element-207">
      <OperationsHeader query={query} onQueryChange={setQuery} data-id="element-208" />
      <main data-id="element-209">
        <KpiRail data-id="element-210" />

        <div className="mx-auto max-w-[1800px] p-3 sm:p-4" data-id="element-211">
          <motion.div initial={reduceMotion ? false : {
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.35
        }} className="mb-3 flex flex-wrap items-center justify-between gap-2 border-x border-slate-800 bg-[#071019] px-3 py-2" data-id="element-212">
            <div className="flex items-center gap-4 overflow-x-auto font-mono text-[8px] tracking-wider text-slate-500" data-id="element-213">
              <span className="flex items-center gap-1.5 whitespace-nowrap" data-id="element-214">
                <SatelliteIcon className="h-3 w-3 text-cyan-400" data-id="element-215" /> AIS LINK{' '}
                <b className="text-lime-300" data-id="element-216">ONLINE</b>
              </span>
              <span className="flex items-center gap-1.5 whitespace-nowrap" data-id="element-217">
                <DatabaseIcon className="h-3 w-3 text-cyan-400" data-id="element-218" /> EVENT BUS{' '}
                <b className="text-lime-300" data-id="element-219">SYNCED</b>
              </span>
              <span className="flex items-center gap-1.5 whitespace-nowrap" data-id="element-220">
                <ActivityIcon className="h-3 w-3 text-amber-400" data-id="element-221" /> LATENCY{' '}
                <b className="text-slate-300" data-id="element-222">42 MS</b>
              </span>
            </div>
            <span className="flex items-center gap-1.5 font-mono text-[8px] text-slate-500" data-id="element-223">
              <CircleDotIcon className="h-3 w-3 text-red-400" data-id="element-224" /> LIVE OPERATIONS
              VIEW
            </span>
          </motion.div>

          <div className="grid gap-3 xl:grid-cols-[minmax(0,3fr)_minmax(300px,1fr)]" data-id="element-225">
            <WorldMap vessels={visibleVessels} selectedId={selectedVessel} onSelect={setSelectedVessel} data-id="element-226" />
            <AlertFeed alerts={alerts} onAcknowledge={id => setAlerts(current => current.filter(alert => alert.id !== id))} data-id="element-227" />
          </div>

          <div className="mt-3 grid gap-3 xl:grid-cols-[minmax(0,3fr)_minmax(300px,1fr)]" data-id="element-228">
            <ShipmentsTable shipments={visibleShipments} filter={filter} onFilterChange={setFilter} selectedId={selectedShipment} onSelect={setSelectedShipment} data-id="element-229" />
            <div className="grid content-start gap-3 sm:grid-cols-2 xl:grid-cols-1" data-id="element-230">
              <PortCongestion data-id="element-231" />
              <CustomsHolds data-id="element-232" />
            </div>
          </div>

          <footer className="mt-3 flex flex-col gap-2 border-t border-slate-800 px-1 pt-3 font-mono text-[8px] tracking-widest text-slate-600 sm:flex-row sm:items-center sm:justify-between" data-id="element-233">
            <p data-id="element-234">
              NEXUS OPS FABRIC · REGION GLOBAL · DATA CLASSIFICATION: INTERNAL
            </p>
            <p data-id="element-235">LAST INGEST 00:00:04 AGO · BUILD 7.42.1</p>
          </footer>
        </div>
      </main>
    </div>;
}